import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
  Menu,
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  Description as FormIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  CloudUpload as UploadIcon,
  Checklist as ChecklistIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import { formService, FormTemplate } from '../services/formService';
import { regulationsApi } from '../services/api';

interface FormDownloadsProps {
  open: boolean;
  onClose: () => void;
}

// Helper function to check if current user is super admin
const isSuperAdmin = (): boolean => {
  try {
    const token = localStorage.getItem('gamora_token');
    if (!token) return false;
    
    const decoded = jwtDecode(token) as any;
    
    // Check role from JWT token (role === 0 means SUPER_ADMIN)
    // Also check localStorage as fallback
    const role = decoded?.role ?? parseInt(localStorage.getItem('gamora_role') || '20', 10);
    
    // Role 0 = SUPER_ADMIN (see App.tsx for role system)
    if (role === 0) {
      return true;
    }
    
    // Fallback: Check email for backward compatibility
    // Only these two email addresses are super admins
    // Use STRICT email matching - no partial matches
    const userEmail = decoded?.email?.toLowerCase() || '';
    const SUPER_ADMIN_EMAILS = ['gandlurm@gmail.com', 'gauriskundliya@gmail.com'];
    
    const isEmailMatch = SUPER_ADMIN_EMAILS.some(
      adminEmail => adminEmail.toLowerCase() === userEmail
    );
    
    return isEmailMatch;
  } catch (error) {
    return false;
  }
};

const FormDownloads: React.FC<FormDownloadsProps> = ({ open, onClose }) => {
  // Initialize admin status immediately
  const [isAdmin, setIsAdmin] = useState(() => isSuperAdmin());
  const [forms, setForms] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRegulation, setSelectedRegulation] = useState<string>('all');
  const [regulations, setRegulations] = useState<any[]>([]);
  const [selectedDeviceClass, setSelectedDeviceClass] = useState<string>('all');
  const [selectedFormType, setSelectedFormType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deviceClassOptions, setDeviceClassOptions] = useState<Array<{value: string, display_name: string}>>([]);
  const [selectedForm, setSelectedForm] = useState<FormTemplate | null>(null);
  const [showFormDetails, setShowFormDetails] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadingChecklist, setUploadingChecklist] = useState<string | null>(null);
  const uploadInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const checklistInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const mdChecklistInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const ivdChecklistInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const multiChecklistInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [showMultiChecklistDialog, setShowMultiChecklistDialog] = useState<{ formId: string; files: File[] } | null>(null);
  const [checklistAssignments, setChecklistAssignments] = useState<{ [fileName: string]: 'generic' | 'md' | 'ivd' }>({});
  const [success, setSuccess] = useState<string | null>(null);
  const [availableJurisdictions, setAvailableJurisdictions] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20, // Reasonable page size for good performance
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Load regulations from regulations table
  const loadRegulations = useCallback(async () => {
    try {
      const data = await regulationsApi.getAllRegulations({
        status: 'active',
        limit: 100,
      });
      // Filter to only US and EU regulations
      const allowedJurisdictions = ['us_fda', 'eu_mdr'];
      const filteredRegs = data.regulations.filter((reg: any) => {
        const jurisdiction = reg.jurisdiction?.toLowerCase();
        return allowedJurisdictions.includes(jurisdiction);
      });
      setRegulations(filteredRegs);
      // Extract unique jurisdictions for backward compatibility
      const uniqueJurisdictions = Array.from(new Set(filteredRegs.map((reg: any) => reg.jurisdiction)));
      setAvailableJurisdictions(uniqueJurisdictions);
    } catch (err) {
      console.error('Error loading regulations:', err);
    }
  }, []);

  // Load forms from database with pagination
  const loadForms = useCallback(async (page: number = 1, reset: boolean = true, limit?: number) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    
    // Use consistent pagination - 20 forms per page for good performance
    const pageLimit = limit || 20;
    
    try {
      // Get jurisdiction from selected regulatory authority
      const getJurisdictionFromRegulation = () => {
        if (selectedRegulation === 'all') return undefined;
        // Find the regulatory authority by ID and use its jurisdiction
        const regulation = regulations.find((r: any) => r.id === selectedRegulation);
        return regulation ? regulation.jurisdiction : undefined;
      };

      const response = await formService.getFormTemplates(
        page,
        pageLimit,
        getJurisdictionFromRegulation(),
        selectedDeviceClass !== 'all' ? selectedDeviceClass : undefined,
        selectedFormType !== 'all' ? selectedFormType : undefined
      );
      
      if (reset) {
        setForms(response.forms);
        // If jurisdictions not loaded from regulations, use available_jurisdictions from API or extract from forms
        if (availableJurisdictions.length === 0) {
          if (response.available_jurisdictions && response.available_jurisdictions.length > 0) {
            setAvailableJurisdictions(response.available_jurisdictions);
          } else {
            // Fallback: extract from loaded forms
            const uniqueJurs = Array.from(new Set(response.forms.map((f: FormTemplate) => f.jurisdiction).filter(Boolean)));
            setAvailableJurisdictions(uniqueJurs);
          }
        }
      } else {
        setForms(prev => [...prev, ...response.forms]);
      }
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error loading forms:', err);
      setError('Failed to load forms. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedRegulation, selectedDeviceClass, selectedFormType, availableJurisdictions.length, regulations]);

  // Load device class options
  const loadDeviceClassOptions = useCallback(async () => {
    try {
      // This would typically come from an API endpoint
      // For now, we'll use a static list
      const options = [
        { value: 'class_i', display_name: 'Class I (Low Risk)' },
        { value: 'class_iia', display_name: 'Class IIa (Low-Moderate Risk)' },
        { value: 'class_iib', display_name: 'Class IIb (Moderate-High Risk)' },
        { value: 'class_iii', display_name: 'Class III (High Risk)' },
      ];
      setDeviceClassOptions(options);
    } catch (err) {
      console.error('Error loading device class options:', err);
    }
  }, []);

  // Load more forms
  const loadMoreForms = useCallback(() => {
    if (pagination.has_next && !loadingMore) {
      loadForms(pagination.page + 1, false);
    }
  }, [pagination.has_next, pagination.page, loadingMore, loadForms]);

  // Check admin status on mount and when dialog opens
  useEffect(() => {
    const adminStatus = isSuperAdmin();
    setIsAdmin(adminStatus);
  }, [open]);

  // Load regulations on mount
  useEffect(() => {
    if (open) {
      loadRegulations();
      
      // Auto-detect country from IP and set default regulatory authority
      const autoDetect = async () => {
        try {
          const detected = await regulationsApi.detectCountryFromIP();
          if (detected.regulation_id) {
            setSelectedRegulation(detected.regulation_id);
          }
        } catch (ipErr) {
        }
      };
      autoDetect();
    }
  }, [open, loadRegulations]);

  // Load data when component mounts
  useEffect(() => {
    if (open) {
      loadForms(1, true);
      loadDeviceClassOptions();
    }
  }, [open, loadDeviceClassOptions, loadForms]);

  // Reload forms when filters change
  useEffect(() => {
    if (open) {
      loadForms(1, true);
    }
  }, [selectedRegulation, selectedDeviceClass, selectedFormType, loadForms, open]);

  // Client-side search filtering (server-side filtering is handled by API)
  const filteredForms = forms.filter(form => {
    const matchesSearch = searchTerm === '' || 
      form.form_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.form_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.form_description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Note: jurisdictions variable removed - using regulations directly in the dropdown

  // Get unique form types from forms
  const formTypes = Array.from(new Set(forms.map(form => form.form_type)));

  const handleDownload = async (formId: string, deviceClass?: 'generic' | 'md' | 'ivd') => {
    setDownloading(formId);
    try {
      if (deviceClass === 'md') {
        window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/forms/${formId}/download/md`, '_blank');
      } else if (deviceClass === 'ivd') {
        window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/forms/${formId}/download/ivd`, '_blank');
      } else {
        await formService.downloadFormTemplate(formId);
      }
    } catch (err) {
      console.error('Error downloading form:', err);
      setError('Failed to download form. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async (formId: string, form: FormTemplate) => {
    setDownloading(formId);
    try {
      const downloads: Promise<void>[] = [];
      
      if (form.s3_url || form.s3_key) {
        downloads.push(formService.downloadFormTemplate(formId).then(() => {}));
      }
      if (form.md_s3_url || form.md_s3_key) {
        downloads.push(new Promise((resolve) => {
          window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/forms/${formId}/download/md`, '_blank');
          setTimeout(resolve, 500);
        }));
      }
      if (form.ivd_s3_url || form.ivd_s3_key) {
        downloads.push(new Promise((resolve) => {
          window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/forms/${formId}/download/ivd`, '_blank');
          setTimeout(resolve, 500);
        }));
      }
      
      await Promise.all(downloads);
      setSuccess(`Downloaded ${downloads.length} form file(s)`);
    } catch (err) {
      console.error('Error downloading forms:', err);
      setError('Failed to download some forms. Please try again.');
    } finally {
      setDownloading(null);
      setDownloadMenuAnchor({});
    }
  };

  const handleDownloadMenuOpen = (formId: string, event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(prev => ({ ...prev, [formId]: event.currentTarget }));
  };

  const handleDownloadMenuClose = (formId: string) => {
    setDownloadMenuAnchor(prev => ({ ...prev, [formId]: null }));
  };

  const getAvailableDownloadCount = (form: FormTemplate): number => {
    let count = 0;
    if (form.s3_url || form.s3_key) count++;
    if (form.md_s3_url || form.md_s3_key) count++;
    if (form.ivd_s3_url || form.ivd_s3_key) count++;
    return count;
  };

  const handleUpload = async (formId: string, file: File, deviceClass?: 'md' | 'ivd') => {
    setUploading(formId);
    setError(null);
    setSuccess(null);
    try {
      const result = deviceClass 
        ? await formService.uploadDeviceClassFormFile(formId, file, deviceClass)
        : await formService.uploadFormFile(formId, file);
      if (result.status === 'success') {
        setSuccess('File uploaded successfully!');
        // Update the form in the current list instead of reloading - preserves pagination
        setForms(prevForms => 
          prevForms.map(form => 
            form.id === formId 
              ? { 
                  ...form, 
                  s3_url: result.s3_url, 
                  s3_key: result.s3_key || form.s3_key || 'uploaded',
                  ...(deviceClass === 'md' ? { md_s3_url: result.s3_url, md_s3_key: result.s3_key } : {}),
                  ...(deviceClass === 'ivd' ? { ivd_s3_url: result.s3_url, ivd_s3_key: result.s3_key } : {})
                }
              : form
          )
        );
      } else {
        setError(result.message || 'Failed to upload file');
      }
    } catch (err: any) {
      console.error('Error uploading form:', err);
      setError(err.message || 'Failed to upload form file. Please try again.');
    } finally {
      setUploading(null);
    }
  };

  const handleFileSelect = (formId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(file.type) && (!fileExt || !allowedExtensions.includes(fileExt))) {
        setError('Invalid file type. Please upload PDF, DOC, DOCX, XLS, or XLSX files.');
        return;
      }
      
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setError('File size exceeds 50MB limit. Please upload a smaller file.');
        return;
      }
      
      handleUpload(formId, file);
    }
    // Reset input
    event.target.value = '';
  };

  const handleDeviceClassFileSelect = (formId: string, deviceClass: 'md' | 'ivd', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(file.type) && (!fileExt || !allowedExtensions.includes(fileExt))) {
        setError('Invalid file type. Please upload PDF, DOC, DOCX, XLS, or XLSX files.');
        return;
      }
      
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setError('File size exceeds 50MB limit. Please upload a smaller file.');
        return;
      }
      
      handleUpload(formId, file, deviceClass);
    }
    // Reset input
    event.target.value = '';
  };

  const triggerDeviceClassFileInput = (formId: string, deviceClass: 'md' | 'ivd') => {
    const inputKey = `${formId}_${deviceClass}`;
    const input = uploadInputRefs.current[inputKey];
    if (input) {
      input.click();
    }
  };

  const triggerFileInput = (formId: string) => {
    const input = uploadInputRefs.current[formId];
    if (input) {
      input.click();
    }
  };

  const triggerChecklistInput = (formId: string) => {
    const input = checklistInputRefs.current[formId];
    if (input) {
      input.click();
    }
  };

  const handleChecklistUpload = async (formId: string, file: File, deviceClass?: 'md' | 'ivd') => {
    setUploadingChecklist(formId);
    setError(null);
    setSuccess(null);
    try {
      const result = deviceClass 
        ? await formService.uploadDeviceClassChecklistDocument(formId, file, deviceClass)
        : await formService.uploadChecklistDocument(formId, file);
      if (result.status === 'success') {
        const deviceText = deviceClass ? `${deviceClass.toUpperCase()} ` : '';
        setSuccess(`${deviceText}Checklist uploaded successfully! ${result.chunks_created || 0} chunks created.`);
        // Update the form in the current list
        setForms(prevForms => 
          prevForms.map(form => 
            form.id === formId 
              ? { 
                  ...form, 
                  ...(deviceClass === 'md' 
                    ? { has_md_checklist: true, md_checklist_s3_url: result.checklist_s3_url }
                    : deviceClass === 'ivd'
                    ? { has_ivd_checklist: true, ivd_checklist_s3_url: result.checklist_s3_url }
                    : { has_checklist: true, checklist_s3_url: result.checklist_s3_url }
                  )
                }
              : form
          )
        );
      } else {
        setError(result.message || 'Failed to upload checklist');
      }
    } catch (err: any) {
      console.error('Error uploading checklist:', err);
      setError(err.message || 'Failed to upload checklist document. Please try again.');
    } finally {
      setUploadingChecklist(null);
    }
  };

  const handleChecklistFileSelect = (formId: string, event: React.ChangeEvent<HTMLInputElement>, deviceClass?: 'md' | 'ivd') => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (only PDF for checklists)
      if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
        setError('Invalid file type. Please upload a PDF file for checklists.');
        return;
      }
      
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setError('File size exceeds 50MB limit. Please upload a smaller file.');
        return;
      }
      
      handleChecklistUpload(formId, file, deviceClass);
    }
    // Reset input
    event.target.value = '';
  };

  const triggerMDChecklistInput = (formId: string) => {
    const input = mdChecklistInputRefs.current[formId];
    if (input) {
      input.click();
    }
  };

  const triggerIVDChecklistInput = (formId: string) => {
    const input = ivdChecklistInputRefs.current[formId];
    if (input) {
      input.click();
    }
  };

  const triggerMultiChecklistInput = (formId: string) => {
    const input = multiChecklistInputRefs.current[formId];
    if (input) {
      input.click();
    }
  };

  const handleMultiChecklistFileSelect = (formId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      // Validate all files are PDFs
      const invalidFiles = files.filter(file => 
        !file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')
      );
      
      if (invalidFiles.length > 0) {
        setError('All checklist files must be PDF format.');
        event.target.value = '';
        return;
      }
      
      // Validate file sizes (max 50MB each)
      const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        setError('Some files exceed 50MB limit. Please upload smaller files.');
        event.target.value = '';
        return;
      }
      
      // Limit to 3 files (Generic, MD, IVD)
      if (files.length > 3) {
        setError('Maximum 3 checklist files allowed (Generic, MD, IVD).');
        event.target.value = '';
        return;
      }
      
      // Initialize assignments - try to infer from filenames
      const assignments: { [fileName: string]: 'generic' | 'md' | 'ivd' } = {};
      files.forEach(file => {
        const nameLower = file.name.toLowerCase();
        if (nameLower.includes('md') && !nameLower.includes('ivd')) {
          assignments[file.name] = 'md';
        } else if (nameLower.includes('ivd')) {
          assignments[file.name] = 'ivd';
        } else {
          assignments[file.name] = 'generic'; // Default to generic
        }
      });
      
      setChecklistAssignments(assignments);
      setShowMultiChecklistDialog({ formId, files });
    }
    // Reset input
    event.target.value = '';
  };

  const handleBulkChecklistUpload = async (formId: string, files: File[], assignments: { [fileName: string]: 'generic' | 'md' | 'ivd' }) => {
    setUploadingChecklist(formId);
    setError(null);
    setSuccess(null);
    
    const results: string[] = [];
    let successCount = 0;
    let failCount = 0;
    
    try {
      // Upload each file with its assigned type
      for (const file of files) {
        const deviceClass = assignments[file.name];
        try {
          const result = deviceClass === 'generic'
            ? await formService.uploadChecklistDocument(formId, file)
            : await formService.uploadDeviceClassChecklistDocument(formId, file, deviceClass as 'md' | 'ivd');
          
          if (result.status === 'success') {
            successCount++;
            const typeText = deviceClass === 'generic' ? 'Generic' : deviceClass.toUpperCase();
            results.push(`✅ ${typeText}: ${file.name} - ${result.chunks_created || 0} chunks created`);
          } else {
            failCount++;
            results.push(`❌ ${file.name}: ${result.message || 'Failed'}`);
          }
        } catch (err: any) {
          failCount++;
          results.push(`❌ ${file.name}: ${err.message || 'Upload failed'}`);
        }
      }
      
      // Update form state
      const form = forms.find(f => f.id === formId);
      if (form) {
        const updatedForm = { ...form };
        files.forEach(file => {
          const deviceClass = assignments[file.name];
          if (deviceClass === 'generic') {
            updatedForm.has_checklist = true;
          } else if (deviceClass === 'md') {
            updatedForm.has_md_checklist = true;
          } else if (deviceClass === 'ivd') {
            updatedForm.has_ivd_checklist = true;
          }
        });
        
        setForms(prevForms => 
          prevForms.map(f => f.id === formId ? updatedForm : f)
        );
      }
      
      if (successCount > 0) {
        setSuccess(`Successfully uploaded ${successCount} checklist(s)! ${results.join('\n')}`);
      }
      if (failCount > 0) {
        setError(`Failed to upload ${failCount} checklist(s). ${results.join('\n')}`);
      }
      
      // Close dialog
      setShowMultiChecklistDialog(null);
      setChecklistAssignments({});
      
    } catch (err: any) {
      console.error('Error in bulk checklist upload:', err);
      setError(err.message || 'Failed to upload checklists. Please try again.');
    } finally {
      setUploadingChecklist(null);
    }
  };

  const handleViewDetails = (form: FormTemplate) => {
    setSelectedForm(form);
    setShowFormDetails(true);
  };

  const clearFilters = () => {
    setSelectedRegulation('all');
    setSelectedDeviceClass('all');
    setSelectedFormType('all');
    setSearchTerm('');
  };

  const getJurisdictionLabel = (jurisdiction: string) => {
    const labels: { [key: string]: string } = {
      'us_fda': 'US (FDA)',
      'eu_mdr': 'EU (MDR)',
      'eu_ivdr': 'EU (IVDR)',
      'australia_tga': 'Australia (TGA)',
      'canada_hc': 'Canada (Health Canada)',
      'japan_pmda': 'Japan (PMDA)',
      'south_korea_mfds': 'South Korea (MFDS)',
      'uk_mhra': 'UK (MHRA)',
      'brazil_anvisa': 'Brazil (ANVISA)',
      'china_nmpa': 'China (NMPA)',
      'other': 'Other',
    };
    return labels[jurisdiction] || jurisdiction;
  };

  const getFormTypeLabel = (formType: string) => {
    const labels: { [key: string]: string } = {
      'application': 'Application',
      'notification': 'Notification',
      'template': 'Template',
      'report': 'Report',
      'declaration': 'Declaration',
    };
    return labels[formType] || formType;
  };

  const getDeviceClassLabel = (deviceClass: string) => {
    const option = deviceClassOptions.find(opt => opt.value === deviceClass);
    return option ? option.display_name : deviceClass;
  };

  if (!open) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: '16px',
          maxHeight: '95vh',
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#fff',
        color: '#111827',
        p: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderBottom: '1px solid #eceef1'
      }}>
        <Box sx={{ 
          p: 1.5, 
          borderRadius: '10px', 
          bgcolor: '#111827'
        }}>
          <FormIcon sx={{ fontSize: 28, color: '#fff' }} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 650,
            fontSize: '1rem',
            mb: 0.5,
            letterSpacing: '-0.02em',
            color: '#111827'
          }}>
            Forms & Templates
          </Typography>
          <Typography variant="body1" sx={{ 
            fontSize: '0.85rem',
            fontWeight: 400,
            color: '#6b7280'
          }}>
            Official compliance forms for medical device regulatory submissions
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f8f9fb' }}>
        {/* Filter Section */}
        <Box sx={{ 
          p: 4, 
          bgcolor: '#f8f9fb',
          borderBottom: '1px solid #eceef1'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '8px', 
                bgcolor: '#111827',
                color: 'white'
              }}>
                <FilterIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 650, 
                color: '#111827',
                fontSize: '1rem'
              }}>
                Search & Filter
              </Typography>
            </Box>
            <Button
              onClick={clearFilters}
              variant="outlined"
              size="small"
              sx={{ 
                borderRadius: '10px',
                px: 2.5,
                py: 1,
                fontSize: '0.8rem',
                fontWeight: 500,
                textTransform: 'none',
                borderColor: '#e5e7eb',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#e5e7eb',
                  backgroundColor: '#f3f4f6'
                }
              }}
            >
              Clear Filters
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <TextField
                fullWidth
                size="small"
                label="Search forms"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, code, or description"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Regulation</InputLabel>
                <Select
                  value={selectedRegulation}
                  onChange={(e) => setSelectedRegulation(e.target.value)}
                  label="Regulation"
                  sx={{ borderRadius: '10px', bgcolor: '#f8f9fb' }}
                >
                  <MenuItem value="all">All Regulatory Authorities</MenuItem>
                  {regulations.length > 0 ? (
                    regulations.map((reg: any) => (
                      <MenuItem key={reg.id} value={reg.id}>
                        {reg.regulatory_authority} ({reg.country})
                      </MenuItem>
                    ))
                  ) : (
                    availableJurisdictions.map((jurisdiction) => (
                      <MenuItem key={jurisdiction} value={jurisdiction}>
                        {getJurisdictionLabel(jurisdiction)}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Device Class</InputLabel>
                <Select
                  value={selectedDeviceClass}
                  onChange={(e) => setSelectedDeviceClass(e.target.value)}
                  label="Device Class"
                  sx={{ borderRadius: '10px', bgcolor: '#f8f9fb' }}
                >
                  <MenuItem value="all">All Device Classes</MenuItem>
                  {deviceClassOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Form Type</InputLabel>
                <Select
                  value={selectedFormType}
                  onChange={(e) => setSelectedFormType(e.target.value)}
                  label="Form Type"
                  sx={{ borderRadius: '10px', bgcolor: '#f8f9fb' }}
                >
                  <MenuItem value="all">All Form Types</MenuItem>
                  {formTypes.map((formType) => (
                    <MenuItem key={formType} value={formType}>
                      {getFormTypeLabel(formType)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Multi-Checklist Assignment Dialog */}
        <Dialog
          open={!!showMultiChecklistDialog}
          onClose={() => {
            setShowMultiChecklistDialog(null);
            setChecklistAssignments({});
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Assign Checklist Types
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select the type for each checklist file (up to 3 files)
            </Typography>
          </DialogTitle>
          <DialogContent>
            {showMultiChecklistDialog && (
              <Box sx={{ mt: 2 }}>
                {showMultiChecklistDialog.files.map((file, index) => (
                  <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #eceef1', borderRadius: '12px' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Checklist Type</InputLabel>
                      <Select
                        value={checklistAssignments[file.name] || 'generic'}
                        onChange={(e) => {
                          setChecklistAssignments(prev => ({
                            ...prev,
                            [file.name]: e.target.value as 'generic' | 'md' | 'ivd'
                          }));
                        }}
                        label="Checklist Type"
                      >
                        <MenuItem value="generic">Generic (Applicable to all device types)</MenuItem>
                        <MenuItem value="md">Medical Device (MD) Specific</MenuItem>
                        <MenuItem value="ivd">In Vitro Diagnostic (IVD) Specific</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowMultiChecklistDialog(null);
                setChecklistAssignments({});
              }}
              disabled={uploadingChecklist === showMultiChecklistDialog?.formId}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (showMultiChecklistDialog) {
                  handleBulkChecklistUpload(
                    showMultiChecklistDialog.formId,
                    showMultiChecklistDialog.files,
                    checklistAssignments
                  );
                }
              }}
              disabled={uploadingChecklist === showMultiChecklistDialog?.formId || !showMultiChecklistDialog}
              startIcon={uploadingChecklist === showMultiChecklistDialog?.formId ? <CircularProgress size={16} /> : <UploadIcon />}
            >
              {uploadingChecklist === showMultiChecklistDialog?.formId ? 'Uploading...' : `Upload ${showMultiChecklistDialog?.files.length || 0} File(s)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbar at top */}
        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: 2 }}
        >
          <Alert 
            onClose={() => setSuccess(null)} 
            severity="success" 
            sx={{ width: '100%', borderRadius: '10px' }}
          >
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: 2 }}
        >
          <Alert 
            onClose={() => setError(null)} 
            severity="error" 
            sx={{ width: '100%', borderRadius: '10px' }}
          >
            {error}
          </Alert>
        </Snackbar>

        {/* Forms Grid */}
        <Box sx={{ p: 4, bgcolor: 'white' }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 8,
              gap: 2
            }}>
              <CircularProgress size={40} sx={{ color: '#111827' }} />
              <Typography variant="body1" color="text.secondary">
                Loading forms...
              </Typography>
            </Box>
          ) : filteredForms.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              px: 4
            }}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                bgcolor: '#f8f9fb', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}>
                <FormIcon sx={{ fontSize: 40, color: '#111827' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                No forms found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or search terms
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 3
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 650, 
                  color: '#111827',
                  fontSize: '1rem'
                }}>
                  {filteredForms.length} forms found
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2 
              }}>
                {filteredForms.map((form) => (
                  <Card 
                    key={form.id}
                    sx={{ 
                      borderRadius: '12px',
                      border: '1px solid #eceef1',
                      boxShadow: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        boxShadow: 'none',
                        borderColor: '#e5e7eb',
                        bgcolor: '#f8f9fb'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 3,
                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                      }}>
                        {/* Left section: Icon and Form Info */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start',
                          gap: 2,
                          flex: '1 1 auto',
                          minWidth: 0
                        }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: '10px', 
                            bgcolor: '#111827',
                            color: 'white',
                            flexShrink: 0
                          }}>
                            <FormIcon sx={{ fontSize: 24 }} />
                          </Box>
                          
                          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1.5, 
                              mb: 1,
                              flexWrap: 'wrap'
                            }}>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 650, 
                                color: '#111827',
                                fontSize: '1rem',
                                lineHeight: 1.3
                              }}>
                                {form.form_name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                  label={form.form_code} 
                                  size="small" 
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    height: 22,
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    bgcolor: '#111827',
                                    color: '#fff',
                                    '& .MuiChip-label': {
                                      px: 1.5
                                    }
                                  }}
                                />
                                <Chip 
                                  label={getJurisdictionLabel(form.jurisdiction)} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    height: 22,
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    borderColor: '#e5e7eb',
                                    color: '#6b7280',
                                    '& .MuiChip-label': {
                                      px: 1.5
                                    }
                                  }}
                                />
                                <Chip 
                                  label={form.file_format?.toUpperCase() || 'PDF'} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    height: 22,
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    borderColor: '#e5e7eb',
                                    color: '#6b7280',
                                    '& .MuiChip-label': {
                                      px: 1.5
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              mb: 1.5, 
                              lineHeight: 1.6,
                              fontSize: '0.85rem',
                              color: '#6b7280'
                            }}>
                              {form.form_description}
                            </Typography>
                            
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: '#9ca3af'
                            }}>
                              {form.file_size ? `${(form.file_size / 1024).toFixed(0)} KB` : 'N/A'} • v{form.version || '1.0'}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Right section: Action Buttons */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          flexShrink: 0,
                          alignItems: 'center',
                          flexWrap: { xs: 'wrap', sm: 'nowrap' }
                        }}>
                          {/* Hidden file input for form upload */}
                          <input
                            type="file"
                            ref={(el) => {
                              if (el) uploadInputRefs.current[form.id] = el;
                            }}
                            onChange={(e) => handleFileSelect(form.id, e)}
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            style={{ display: 'none' }}
                          />
                          
                          {/* Hidden file input for MD upload */}
                          <input
                            type="file"
                            ref={(el) => {
                              if (el) uploadInputRefs.current[`${form.id}_md`] = el;
                            }}
                            onChange={(e) => handleDeviceClassFileSelect(form.id, 'md', e)}
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            style={{ display: 'none' }}
                          />
                          
                          {/* Hidden file input for IVD upload */}
                          <input
                            type="file"
                            ref={(el) => {
                              if (el) uploadInputRefs.current[`${form.id}_ivd`] = el;
                            }}
                            onChange={(e) => handleDeviceClassFileSelect(form.id, 'ivd', e)}
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            style={{ display: 'none' }}
                          />
                          
                          {/* Hidden file input for generic checklist upload */}
                          <input
                            type="file"
                            ref={(el) => {
                              if (el) checklistInputRefs.current[form.id] = el;
                            }}
                            onChange={(e) => handleChecklistFileSelect(form.id, e)}
                            accept=".pdf"
                            style={{ display: 'none' }}
                          />
                          
                          {/* Hidden file input for MD checklist upload */}
                          <input
                            type="file"
                            ref={(el) => {
                              if (el) mdChecklistInputRefs.current[form.id] = el;
                            }}
                            onChange={(e) => handleChecklistFileSelect(form.id, e, 'md')}
                            accept=".pdf"
                            style={{ display: 'none' }}
                          />
                          
                          {/* Hidden file input for IVD checklist upload */}
                          <input
                            type="file"
                            ref={(el) => {
                              if (el) ivdChecklistInputRefs.current[form.id] = el;
                            }}
                            onChange={(e) => handleChecklistFileSelect(form.id, e, 'ivd')}
                            accept=".pdf"
                            style={{ display: 'none' }}
                          />
                          
                          {/* Hidden file input for multiple checklist upload */}
                          <input
                            type="file"
                            ref={(el) => {
                              if (el) multiChecklistInputRefs.current[form.id] = el;
                            }}
                            onChange={(e) => handleMultiChecklistFileSelect(form.id, e)}
                            accept=".pdf"
                            multiple
                            style={{ display: 'none' }}
                          />
                          
                          {/* Details button - visible to all users */}
                          <Button
                            variant="outlined"
                            onClick={() => handleViewDetails(form)}
                            size="small"
                            sx={{ 
                              minWidth: 'auto', 
                              px: 2,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderColor: '#e5e7eb',
                              color: '#6b7280',
                              '&:hover': {
                                borderColor: '#e5e7eb',
                                backgroundColor: '#f3f4f6'
                              }
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            Details
                          </Button>
                          
                          {/* Upload buttons - ONLY visible to super admins */}
                          {/* Normal users will NOT see these buttons */}
                          {isAdmin && (
                            <>
                              <Button
                                variant="outlined"
                                onClick={() => triggerFileInput(form.id)}
                                size="small"
                                disabled={uploading === form.id}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 2,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderColor: form.s3_key ? '#3b82f6' : '#10b981',
                              color: form.s3_key ? '#3b82f6' : '#10b981',
                              '&:hover': {
                                borderColor: form.s3_key ? '#2563eb' : '#059669',
                                backgroundColor: '#f3f4f6'
                              },
                              '&:disabled': {
                                borderColor: '#e5e7eb',
                                color: '#9ca3af'
                              }
                            }}
                          >
                            {uploading === form.id ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <UploadIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                {form.s3_key ? 'Re-upload' : 'Upload'}
                              </>
                            )}
                          </Button>
                          
                          {/* MD (Medical Device) Upload Button */}
                          <Button
                            variant="outlined"
                            onClick={() => triggerDeviceClassFileInput(form.id, 'md')}
                            size="small"
                            disabled={uploading === form.id}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1.5,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderColor: form.md_s3_key ? '#ef4444' : '#f59e0b',
                              color: form.md_s3_key ? '#ef4444' : '#f59e0b',
                              '&:hover': {
                                borderColor: form.md_s3_key ? '#dc2626' : '#d97706',
                                backgroundColor: '#f3f4f6'
                              },
                              '&:disabled': {
                                borderColor: '#e5e7eb',
                                color: '#9ca3af'
                              }
                            }}
                            title={form.md_s3_key ? "Re-upload MD form" : "Upload Medical Device (MD) specific form"}
                          >
                            {uploading === form.id ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <UploadIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {form.md_s3_key ? 'MD ✓' : 'Upload MD'}
                              </>
                            )}
                          </Button>
                          
                          {/* IVD (In Vitro Diagnostic) Upload Button */}
                          <Button
                            variant="outlined"
                            onClick={() => triggerDeviceClassFileInput(form.id, 'ivd')}
                            size="small"
                            disabled={uploading === form.id}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1.5,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderColor: form.ivd_s3_key ? '#374151' : '#6b7280',
                              color: form.ivd_s3_key ? '#374151' : '#6b7280',
                              '&:hover': {
                                borderColor: form.ivd_s3_key ? '#111827' : '#374151',
                                backgroundColor: '#f3f4f6'
                              },
                              '&:disabled': {
                                borderColor: '#e5e7eb',
                                color: '#9ca3af'
                              }
                            }}
                            title={form.ivd_s3_key ? "Re-upload IVD form" : "Upload In Vitro Diagnostic (IVD) specific form"}
                          >
                            {uploading === form.id ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <UploadIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {form.ivd_s3_key ? 'IVD ✓' : 'Upload IVD'}
                              </>
                            )}
                          </Button>
                          
                          {/* Generic Checklist Upload */}
                          <Button
                            variant="outlined"
                            onClick={() => triggerChecklistInput(form.id)}
                            size="small"
                            disabled={uploadingChecklist === form.id}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1.5,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderColor: form.has_checklist ? '#374151' : '#6b7280',
                              color: form.has_checklist ? '#374151' : '#6b7280',
                              '&:hover': {
                                borderColor: form.has_checklist ? '#111827' : '#374151',
                                backgroundColor: '#f3f4f6'
                              },
                              '&:disabled': {
                                borderColor: '#e5e7eb',
                                color: '#9ca3af'
                              }
                            }}
                            title={form.has_checklist ? "Re-upload generic checklist" : "Upload generic checklist (PDF)"}
                          >
                            {uploadingChecklist === form.id ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                Processing...
                              </>
                            ) : (
                              <>
                                <ChecklistIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {form.has_checklist ? 'Generic ✓' : 'Generic'}
                              </>
                            )}
                          </Button>

                          {/* MD Checklist Upload */}
                          <Button
                            variant="outlined"
                            onClick={() => triggerMDChecklistInput(form.id)}
                            size="small"
                            disabled={uploadingChecklist === form.id}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1.5,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderColor: form.has_md_checklist ? '#ef4444' : '#f59e0b',
                              color: form.has_md_checklist ? '#ef4444' : '#f59e0b',
                              '&:hover': {
                                borderColor: form.has_md_checklist ? '#dc2626' : '#d97706',
                                backgroundColor: '#f3f4f6'
                              },
                              '&:disabled': {
                                borderColor: '#e5e7eb',
                                color: '#9ca3af'
                              }
                            }}
                            title={form.has_md_checklist ? "Re-upload MD checklist" : "Upload Medical Device (MD) checklist (PDF)"}
                          >
                            {uploadingChecklist === form.id ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                Processing...
                              </>
                            ) : (
                              <>
                                <ChecklistIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {form.has_md_checklist ? 'MD ✓' : 'MD'}
                              </>
                            )}
                          </Button>

                          {/* IVD Checklist Upload */}
                          <Button
                            variant="outlined"
                            onClick={() => triggerIVDChecklistInput(form.id)}
                            size="small"
                            disabled={uploadingChecklist === form.id}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1.5,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderColor: form.has_ivd_checklist ? '#374151' : '#6b7280',
                              color: form.has_ivd_checklist ? '#374151' : '#6b7280',
                              '&:hover': {
                                borderColor: form.has_ivd_checklist ? '#111827' : '#374151',
                                backgroundColor: '#f3f4f6'
                              },
                              '&:disabled': {
                                borderColor: '#e5e7eb',
                                color: '#9ca3af'
                              }
                            }}
                            title={form.has_ivd_checklist ? "Re-upload IVD checklist" : "Upload In Vitro Diagnostic (IVD) checklist (PDF)"}
                          >
                            {uploadingChecklist === form.id ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                Processing...
                              </>
                            ) : (
                              <>
                                <ChecklistIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                {form.has_ivd_checklist ? 'IVD ✓' : 'IVD'}
                              </>
                            )}
                          </Button>

                          {/* Upload Multiple Checklists Button */}
                          <Button
                            variant="outlined"
                            onClick={() => triggerMultiChecklistInput(form.id)}
                            size="small"
                            disabled={uploadingChecklist === form.id}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1.5,
                              py: 1,
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              borderColor: '#10b981',
                              color: '#10b981',
                              borderWidth: 2,
                              '&:hover': {
                                borderColor: '#059669',
                                backgroundColor: '#f3f4f6',
                                borderWidth: 2
                              },
                              '&:disabled': {
                                borderColor: '#e5e7eb',
                                color: '#9ca3af',
                                borderWidth: 1
                              }
                            }}
                            title="Upload multiple checklists at once (Generic, MD, IVD) - up to 3 PDF files"
                          >
                            {uploadingChecklist === form.id ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <UploadIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                Upload All
                              </>
                            )}
                          </Button>
                            </>
                          )}
                          
                          {/* Download button(s) - visible to all users */}
                          {(() => {
                            const availableCount = getAvailableDownloadCount(form);
                            const hasMultiple = availableCount > 1;
                            
                            if (hasMultiple) {
                              return (
                                <>
                                  <Button
                                    variant="contained"
                                    startIcon={downloading === form.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                                    endIcon={<ArrowDropDownIcon />}
                                    onClick={(e) => handleDownloadMenuOpen(form.id, e)}
                                    disabled={downloading === form.id}
                                    size="small"
                                    sx={{ 
                                      borderRadius: '10px',
                                      px: 2.5,
                                      py: 1,
                                      fontSize: '0.8rem',
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      bgcolor: '#111827',
                                      boxShadow: 'none',
                                      '&:hover': {
                                        bgcolor: '#1f2937',
                                        boxShadow: 'none'
                                      },
                                      '&:disabled': {
                                        bgcolor: '#e5e7eb',
                                        color: '#9ca3af',
                                        boxShadow: 'none'
                                      }
                                    }}
                                  >
                                    {downloading === form.id ? 'Downloading...' : `Download (${availableCount})`}
                                  </Button>
                                  <Menu
                                    anchorEl={downloadMenuAnchor[form.id]}
                                    open={!!downloadMenuAnchor[form.id]}
                                    onClose={() => handleDownloadMenuClose(form.id)}
                                    anchorOrigin={{
                                      vertical: 'bottom',
                                      horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                      vertical: 'top',
                                      horizontal: 'right',
                                    }}
                                  >
                                    {availableCount === 3 && (
                                      <MenuItem 
                                        onClick={() => {
                                          handleDownloadAll(form.id, form);
                                          handleDownloadMenuClose(form.id);
                                        }}
                                        disabled={downloading === form.id}
                                      >
                                        <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Download All ({availableCount})
                                      </MenuItem>
                                    )}
                                    {(form.s3_url || form.s3_key) && (
                                      <MenuItem 
                                        onClick={() => {
                                          handleDownload(form.id, 'generic');
                                          handleDownloadMenuClose(form.id);
                                        }}
                                        disabled={downloading === form.id}
                                      >
                                        <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Generic Form
                                      </MenuItem>
                                    )}
                                    {(form.md_s3_url || form.md_s3_key) && (
                                      <MenuItem 
                                        onClick={() => {
                                          handleDownload(form.id, 'md');
                                          handleDownloadMenuClose(form.id);
                                        }}
                                        disabled={downloading === form.id}
                                      >
                                        <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Medical Device (MD)
                                      </MenuItem>
                                    )}
                                    {(form.ivd_s3_url || form.ivd_s3_key) && (
                                      <MenuItem 
                                        onClick={() => {
                                          handleDownload(form.id, 'ivd');
                                          handleDownloadMenuClose(form.id);
                                        }}
                                        disabled={downloading === form.id}
                                      >
                                        <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
                                        In Vitro Diagnostic (IVD)
                                      </MenuItem>
                                    )}
                                  </Menu>
                                </>
                              );
                            } else {
                              // Single download - show simple button
                              return (
                                <Button
                                  variant="contained"
                                  startIcon={downloading === form.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                                  onClick={() => handleDownload(form.id)}
                                  disabled={downloading === form.id}
                                  size="small"
                                  sx={{ 
                                    borderRadius: '10px',
                                    px: 2.5,
                                    py: 1,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    bgcolor: '#111827',
                                    boxShadow: 'none',
                                    '&:hover': {
                                      bgcolor: '#1f2937',
                                      boxShadow: 'none'
                                    },
                                    '&:disabled': {
                                      bgcolor: '#e5e7eb',
                                      color: '#9ca3af',
                                      boxShadow: 'none'
                                    }
                                  }}
                                >
                                  {downloading === form.id ? 'Downloading...' : 'Download'}
                                </Button>
                              );
                            }
                          })()}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </>
          )}

          {/* Load More Button */}
          {pagination.has_next && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={loadMoreForms}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  borderColor: '#e5e7eb',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#e5e7eb',
                    backgroundColor: '#f3f4f6'
                  }
                }}
              >
                {loadingMore ? 'Loading more forms...' : 'Load More Forms'}
              </Button>
            </Box>
          )}

          {/* Pagination Info */}
          {forms.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {forms.length} of {pagination.total} forms
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #eceef1' }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#e5e7eb', backgroundColor: '#f3f4f6' } }}>
          Close
        </Button>
      </DialogActions>

      {/* Form Details Dialog */}
      <Dialog 
        open={showFormDetails} 
        onClose={() => setShowFormDetails(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)' }
        }}
      >
        {selectedForm && (
          <>
            <DialogTitle sx={{ 
              bgcolor: '#fff',
              color: '#111827',
              borderBottom: '1px solid #eceef1',
              p: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ p: 1, borderRadius: '8px', bgcolor: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FormIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 650, fontSize: '1rem', color: '#111827' }}>
                  {selectedForm.form_name}
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {selectedForm.form_description}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                  <Chip 
                    label={selectedForm.form_code} 
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 22, borderRadius: '6px', bgcolor: '#111827', color: '#fff', fontWeight: 600 }}
                  />
                  <Chip 
                    label={getJurisdictionLabel(selectedForm.jurisdiction)} 
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 22, borderRadius: '6px', borderColor: '#e5e7eb', color: '#6b7280' }}
                  />
                  <Chip 
                    label={getFormTypeLabel(selectedForm.form_type)} 
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 22, borderRadius: '6px', borderColor: '#e5e7eb', color: '#6b7280' }}
                  />
                </Box>
              </Box>

              {/* Required Fields */}
              {selectedForm.required_fields && selectedForm.required_fields.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650, mb: 1, color: '#111827', fontSize: '0.9rem' }}>
                    Required Fields
                  </Typography>
                  <List dense>
                    {selectedForm.required_fields.map((field, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={field.label}
                          secondary={`Type: ${field.type}${field.max_length ? `, Max Length: ${field.max_length}` : ''}`}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Optional Fields */}
              {selectedForm.optional_fields && selectedForm.optional_fields.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650, mb: 1, color: '#111827', fontSize: '0.9rem' }}>
                    Optional Fields
                  </Typography>
                  <List dense>
                    {selectedForm.optional_fields.map((field, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={field.label}
                          secondary={`Type: ${field.type}${field.max_length ? `, Max Length: ${field.max_length}` : ''}`}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Supporting Documents */}
              {selectedForm.supporting_documents && selectedForm.supporting_documents.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650, mb: 1, color: '#111827', fontSize: '0.9rem' }}>
                    Supporting Documents
                  </Typography>
                  <List dense>
                    {selectedForm.supporting_documents.map((doc, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <DescriptionIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Applicable Device Classes */}
              {selectedForm.applicable_device_classes && selectedForm.applicable_device_classes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650, mb: 1, color: '#111827', fontSize: '0.9rem' }}>
                    Applicable Device Classes
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {selectedForm.applicable_device_classes.map((deviceClass, index) => (
                      <Chip
                        key={index}
                        label={getDeviceClassLabel(deviceClass)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #eceef1' }}>
              <Button onClick={() => setShowFormDetails(false)} variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#e5e7eb', backgroundColor: '#f3f4f6' } }}>
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={downloading === selectedForm.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={() => handleDownload(selectedForm.id)}
                disabled={downloading === selectedForm.id}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', bgcolor: '#111827', boxShadow: 'none', '&:hover': { bgcolor: '#1f2937', boxShadow: 'none' }, '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}
              >
                {downloading === selectedForm.id ? 'Downloading...' : 'Download Form'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Dialog>
  );
};

export default FormDownloads;